/* eslint-disable jsx-a11y/aria-role */
import React from 'react';
import { Platform } from 'react-native';
import PropTypes from 'prop-types';
import {ActivableRenderer} from '../hoc/ActivableRenderer';
import omit from "lodash/omit";

import { Modal as NBModal } from 'native-base';
import { Button as InjectButton } from '~/native-ui/Button';
import { withResponsiveFlags } from '../../hoc/withResponsiveProps';

export const dialogPropTypes = {
    actions: PropTypes.oneOfType([
        PropTypes.arrayOf(
            PropTypes.shape({
                className: PropTypes.string,
                label: PropTypes.string,
                children: PropTypes.node,
            })
        ),
        PropTypes.node
    ]),
    _body: PropTypes.shape(),
    _close: PropTypes.shape(),
    showCloseButton: PropTypes.bool,
    active: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
    onOverlayClick: PropTypes.func,
    onEscKeyDown: PropTypes.func,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.node,]),
    type: PropTypes.string,
    footerButtonGroupProps: PropTypes.any,
    initialFocusRef: PropTypes.oneOfType([
        // Either a function
        PropTypes.func, 
        // Or the instance of a DOM native element (see the note about SSR)
        PropTypes.shape({ current: PropTypes.any })
    ])        
}

const factory = ({ Modal, Button, PortalProvider }) => {

    class Dialog extends React.PureComponent {

        
        renderTitle() {
            const { title, } = this.props;
            if (!title) {
                return null;
            }

            return (
                <Modal.Header>{ title }</Modal.Header>
            )
        }

        renderActions() {
            let { actions, } = this.props;

            if (!actions) return null;

            if (actions instanceof Array) {
                actions = actions.map((action, idx) => {
                    return <Button key={idx} {...action} />; // eslint-disable-line
                });
            }

            return (
                <Modal.Footer>
                    <Button.ButtonGroup {...(this.props.footerButtonGroupProps ?? {})} space={2}>
                        {actions}
                    </Button.ButtonGroup>
                </Modal.Footer>
            )
        }


        setModelBodyRef = (node) => {
            if (Platform.OS !== 'web') return;

            if (node) {
                document.body.classList.add("dialog-open");
                return;
            }

            document.body.classList.remove("dialog-open");
        }

        render() {
            const { _body, _close, showCloseButton, isSmallScreen, ...props } = this.props;


            const sizeMap = {
                "fullscreen": "full",
                "extra-large": "xl",
                "large": "lg",
                "medium": "md",
                "small": "sm",
                "extra-small": "xs",
                "auto": "auto",

                "fullscreen-auto": "fullScreen",
                "extra-large-auto": "xl",
                "large-auto": "lg",
                "medium-auto": "md",
                "small-auto": "sm",
                "extra-small-auto": "xs",
            }

            let type = this.props.type;
            let rounded;

            if (this.props.isSmallScreen && this.props.type.endsWith("-auto")) {
                type = 'fullscreen-auto';
                rounded="0"
            }

            const modalProps = {
                isOpen: props.active,
                onClose: props.onOverlayClick,
                size: sizeMap[type],
                initialFocusRef: props.initialFocusRef,
                finalFocusRef: props.finalFocusRef,
            }

            const modalContentProps = {
                rounded,
                ...omit(props, ...Object.keys(dialogPropTypes))
            }

            return (
                <NBModal { ...modalProps }>
                    <Modal.Content { ...modalContentProps }>
                        { showCloseButton && <Modal.CloseButton { ..._close } /> }
                        <PortalProvider>
                            {this.renderTitle()}

                            <Modal.Body ref={this.setModelBodyRef} { ..._body }>
                                {props.children}
                            </Modal.Body>

                            {this.renderActions()}
                        </PortalProvider>
                    </Modal.Content>
                </NBModal>
            );
        }
    };

    Dialog.propTypes = dialogPropTypes;

    Dialog.defaultProps = {
        showCloseButton: true,
        active: false,
        type: 'normal',
        _body: {},
        _close: {},
    };

    return ActivableRenderer()(withResponsiveFlags(Dialog, { includeKeys: ["isSmallScreen"] }));
};

export const dialogFactory = (components = {}) => factory({ Modal: NBModal, Button: InjectButton, PortalProvider: React.Fragment, ...components });

const Dialog = dialogFactory();
export default (Dialog);
export { Dialog };
